/**
 * Local store — Dexie (IndexedDB) is the source of truth on the device.
 * The UI reads and writes here; the sync engine reconciles with Supabase.
 *
 * Works on web and inside the Capacitor WebView (WKWebView persists
 * IndexedDB in the app container). Call `requestPersistentStorage()` on
 * launch. Do not add the SQLite plugin unless a device test shows eviction.
 */
import Dexie, { type Table } from 'dexie';
import type { OutboxEntry, SyncStateRow, SyncTable } from '../types';

export interface KvRow {
  key: string;
  value: unknown;
}

/**
 * A photo/file taken on this device, waiting to reach Supabase Storage. The
 * row that references it (e.g. a maintenance request's `photos`) already
 * holds the final storage path, so the record syncs on its own and the file
 * follows when there's signal. Never synced as a row.
 */
export interface PendingUpload {
  /** Storage path, `{company}/{user}/{kind}/{uuid}.jpg` — also the key. */
  path: string;
  bucket: string;
  blob: Blob;
  content_type: string;
  created_at: number;
  attempts: number;
  last_error: string | null;
}

/**
 * Dexie index definitions for every synced table, keyed by table name. Must
 * match the server whitelist in supabase/migrations/0009_sync.sql. Each needs
 * at least `id, company_id, updated_at, [company_id+deleted_at]`.
 * Changing an index means bumping the Dexie version below.
 */
export const SYNCED_STORES: Record<string, string> = {
  terminals: 'id, company_id, updated_at, [company_id+deleted_at]',
  member_terminals: 'id, company_id, updated_at, [company_id+deleted_at], user_id, terminal_id',
  trucks: 'id, company_id, updated_at, [company_id+deleted_at], unit_number',
  routes: 'id, company_id, updated_at, [company_id+deleted_at]',
  schedule_assignments: 'id, company_id, updated_at, [company_id+deleted_at], work_date, user_id',
  schedule_days: 'id, company_id, updated_at, [company_id+deleted_at]',
  calendar_events: 'id, company_id, updated_at, [company_id+deleted_at]',
  uniform_items: 'id, company_id, updated_at, [company_id+deleted_at]',
  document_types: 'id, company_id, updated_at, [company_id+deleted_at]',
  shippers: 'id, company_id, updated_at, [company_id+deleted_at]',
  shipper_contacts: 'id, company_id, updated_at, [company_id+deleted_at], shipper_id',
  vehicle_documents: 'id, company_id, updated_at, [company_id+deleted_at], truck_id',
  truck_favorites: 'id, company_id, updated_at, [company_id+deleted_at], user_id, truck_id',
  resource_pages: 'id, company_id, updated_at, [company_id+deleted_at]',
  lineup_lines: 'id, company_id, updated_at, [company_id+deleted_at]',
  lineup_sections: 'id, company_id, updated_at, [company_id+deleted_at]',
  lineup_spots: 'id, company_id, updated_at, [company_id+deleted_at], section_id',
  lineup_assignments: 'id, company_id, updated_at, [company_id+deleted_at], spot_id, truck_id',
  handbook_versions: 'id, company_id, updated_at, [company_id+deleted_at]',
  consent_versions: 'id, company_id, updated_at, [company_id+deleted_at]',
  maintenance_requests: 'id, company_id, updated_at, [company_id+deleted_at], user_id, truck_id, status',
  timeoff_requests: 'id, company_id, updated_at, [company_id+deleted_at], user_id, status, start_date',
  uniform_requests: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  driver_documents: 'id, company_id, updated_at, [company_id+deleted_at], user_id, document_type_id',
  driver_profiles: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  schedule_availability: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  accident_reports: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  accident_photos: 'id, company_id, updated_at, [company_id+deleted_at], report_id',
  op201_forms: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  bulk_pickups: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
  handbook_acks: 'id, company_id, updated_at, [company_id+deleted_at]',
  consent_signatures: 'id, company_id, updated_at, [company_id+deleted_at]',
  broadcast_reads: 'id, company_id, updated_at, [company_id+deleted_at], broadcast_id',
  broadcasts: 'id, company_id, updated_at, [company_id+deleted_at], sent_at',
  // managers' and mechanics' maintenance data (drivers receive none — RLS)
  work_orders: 'id, company_id, updated_at, [company_id+deleted_at], truck_id, status',
  parts: 'id, company_id, updated_at, [company_id+deleted_at], truck_id',
  odometer_readings: 'id, company_id, updated_at, [company_id+deleted_at], truck_id',
  recurring_tasks: 'id, company_id, updated_at, [company_id+deleted_at]',
  recurring_task_trucks: 'id, company_id, updated_at, [company_id+deleted_at], task_id, truck_id',
  schedule_excluded: 'id, company_id, updated_at, [company_id+deleted_at], user_id',
};

export const SYNC_TABLES: SyncTable[] = Object.keys(SYNCED_STORES);

export class RouteDb extends Dexie {
  outbox!: Table<OutboxEntry, string>;
  sync_state!: Table<SyncStateRow, SyncTable>;
  /** Small per-device state (current company, UI prefs). Never synced. */
  kv!: Table<KvRow, string>;
  pending_uploads!: Table<PendingUpload, string>;

  /** `extraStores` lets tests declare throwaway synced tables. */
  constructor(name = 'routeback', extraStores: Record<string, string> = {}) {
    super(name);
    // v2 (2026-10-14): pending_uploads for offline photos. v3: lineup_lines (van lines). v4: truck_favorites. v5: maintenance (work orders, parts, odometer, recurring). v6: schedule_excluded. v7: broadcasts (messages) + reads by message.
    this.version(7).stores({
      ...SYNCED_STORES,
      ...extraStores,
      outbox: 'id, table, row_id, queued_at, rejected, [table+row_id]',
      sync_state: 'table',
      kv: 'key',
      pending_uploads: 'path, created_at',
    });
  }

  table_(name: SyncTable): Table<{ id: string; company_id: string; updated_at: string; deleted_at: string | null }, string> {
    return this.table(name) as never;
  }
}

let _db: RouteDb | null = null;
export function getDb(): RouteDb {
  if (!_db) _db = new RouteDb();
  return _db;
}
/** Tests: swap in a fresh database. */
export function setDb(db: RouteDb | null): void {
  _db = db;
}
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    const nav = globalThis.navigator as Navigator | undefined;
    if (nav?.storage?.persist) return await nav.storage.persist();
  } catch {
    /* ignore */
  }
  return false;
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const r = await getDb().kv.get(key);
  return r?.value as T | undefined;
}
export async function kvSet(key: string, value: unknown): Promise<void> {
  await getDb().kv.put({ key, value });
}
